/**
 * Sticky sign-up bar (sections/sticky-signup.liquid).
 *
 * Tracks a few facts about the page and the visitor, then asks shouldShow() whether the bar
 * belongs on screen. While it is visible, the body gets bottom padding equal to the space the bar
 * covers so the footer and last section are never trapped underneath it.
 *
 * Two states: the full bar with the form, or minimised to a small tab. The chevron minimises
 * (remembered for the browser session); the tab brings the full bar back.
 */
const MINIMIZED_KEY = 'theme:sticky-signup-minimized';

class StickySignup extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.state = {
      pastHero: false,
      contactInView: false,
      minimized: readMinimized(),
      retired: false,
      submitted: this.hasAttribute('data-submitted'),
      hasError: Boolean(this.querySelector('.sticky-signup__error')),
      designMode: this.hasAttribute('data-design-mode'),
    };

    window.addEventListener('scroll', () => this.updatePastHero(), { passive: true, signal });
    this.querySelector('[data-sticky-signup-minimize]')?.addEventListener('click', () => this.setMinimized(true), { signal });
    this.querySelector('[data-sticky-signup-restore]')?.addEventListener('click', () => this.setMinimized(false), { signal });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hasAttribute('data-minimized')) this.setMinimized(true);
    }, { signal });

    const contactSection = document.querySelector('.shopify-section--contact');
    if (contactSection) {
      this.contactObserver = new IntersectionObserver(([entry]) => {
        this.setState({ contactInView: Boolean(entry?.isIntersecting) });
      });
      this.contactObserver.observe(contactSection);
    }

    this.resizeObserver = new ResizeObserver(() => this.syncBodyPadding());
    this.resizeObserver.observe(this);

    // A successful sign-up stays on screen long enough to read, then the bar retires.
    if (this.state.submitted) {
      this.setState({ pastHero: true });
      this.retireTimer = setTimeout(() => this.setState({ submitted: false, retired: true }), 4000);
    }

    this.updatePastHero();
    this.setState({});
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.contactObserver?.disconnect();
    this.resizeObserver.disconnect();
    clearTimeout(this.retireTimer);
    document.body.style.removeProperty('padding-block-end');
  }

  updatePastHero() {
    const pastHero = window.scrollY > window.innerHeight * 0.6;
    if (pastHero !== this.state.pastHero) this.setState({ pastHero });
  }

  setMinimized(minimized) {
    try {
      if (minimized) {
        sessionStorage.setItem(MINIMIZED_KEY, 'true');
      } else {
        sessionStorage.removeItem(MINIMIZED_KEY);
      }
    } catch {}

    this.toggleAttribute('data-minimized', minimized);
    // Minimising also clears an error, otherwise the error rule would pull the full bar back up.
    this.setState({ minimized, hasError: minimized ? false : this.state.hasError });

    const focusTarget = minimized ? '[data-sticky-signup-restore]' : '[data-sticky-signup-minimize]';
    this.querySelector(focusTarget)?.focus({ preventScroll: true });
  }

  setState(patch) {
    Object.assign(this.state, patch);
    const visible = shouldShow(this.state);
    this.classList.toggle('is-visible', visible);
    // An error or a fresh sign-up message needs the full bar, even if it was minimised earlier.
    this.toggleAttribute('data-minimized', this.state.minimized && !this.state.hasError && !this.state.submitted);
    this.syncBodyPadding();
  }

  syncBodyPadding() {
    if (this.classList.contains('is-visible')) {
      const offset = parseFloat(getComputedStyle(this).bottom) || 0;
      const covered = this.hasAttribute('data-minimized')
        ? this.querySelector('[data-sticky-signup-restore]')?.offsetHeight ?? 0
        : this.offsetHeight;
      document.body.style.setProperty('padding-block-end', `${covered + offset * 2}px`);
    } else {
      document.body.style.removeProperty('padding-block-end');
    }
  }
}

/**
 * Decides whether the bar is on screen. Called on every state change.
 *
 * state.pastHero      — visitor has scrolled past ~60% of the first screen (the countdown)
 * state.contactInView — the full contact section is on screen
 * state.minimized     — shrunk to the small tab with the chevron (the tab still shows)
 * state.retired       — signed up; the thank-you message has had its time on screen
 * state.submitted     — just signed up; the bar is showing its thank-you message
 * state.hasError      — the last submit from this bar came back with an error to show
 * state.designMode    — inside the Shopify theme editor
 */
function shouldShow(state) {
  if (state.designMode || state.hasError || state.submitted) return true;
  if (state.retired || state.contactInView) return false;
  return state.pastHero;
}

function readMinimized() {
  try {
    return sessionStorage.getItem(MINIMIZED_KEY) === 'true';
  } catch {
    return false;
  }
}

if (!customElements.get('sticky-signup')) {
  customElements.define('sticky-signup', StickySignup);
}
